import { useRouter } from 'vue-router';
import type { FrontendProposal, NavigateTarget } from '@/api/copilot';
import { useTheme } from './useTheme';
import { useDeveloperMode } from './useDeveloperMode';

/**
 * Applies a frontend-scoped Copilot proposal locally — navigation, theme,
 * developer-mode toggle. Backend-scoped proposals never reach this path;
 * they go to POST /v1/copilot/proposals/apply.
 *
 * Per ADR-0043 / philosophy §2.3, `apply` returns an undo function for
 * trivially-reversible kinds (`set_theme`, `toggle_developer_mode`). The
 * SPA uses this for silent-apply: when a low-stakes proposal arrives the
 * UI applies it immediately and renders a slim "Готово / Отменить" card
 * powered by the returned undo callback. Kinds that have no meaningful
 * undo (navigation, clarification, simulated-call) return `null`.
 */
export type ProposalUndo = (() => Promise<void> | void) | null;

export function useFrontendProposalApplier() {
  const router = useRouter();
  const theme = useTheme();
  const dev = useDeveloperMode();

  async function apply(proposal: FrontendProposal): Promise<ProposalUndo> {
    if (proposal.kind === 'navigate') {
      await navigate(proposal.target, proposal.projectId);
      // Navigation has no clean inline undo — the user can navigate back
      // manually. We return null so silent-apply skips this kind for now.
      return null;
    }
    if (proposal.kind === 'set_theme') {
      const previous = theme.preference.value;
      theme.setPreference(proposal.theme);
      return () => {
        theme.preference.value = previous;
      };
    }
    if (proposal.kind === 'toggle_developer_mode') {
      const previous = dev.enabled.value;
      dev.set(proposal.enabled);
      return () => {
        dev.set(previous);
      };
    }
    if (proposal.kind === 'clarification_question') {
      // Clarification questions are pure UI sugar — the GoalsDialogPage
      // renders them as suggestion chips inline, so the global frontend
      // proposal applier doesn't need to do anything here.
      return null;
    }
    if (proposal.kind === 'simulated_call_transcript') {
      // Simulated call transcripts render as a chat-bubble card inline
      // inside the assistant's GoalsDialogPage turn. Pure preview — no
      // global apply behavior; the user reacts in chat.
      return null;
    }
    if (proposal.kind === 'artifact_walkthrough') {
      // ADR-0047 §"Explain-in-reverse". Composite preview card —
      // business-summary + nested simulated call. Pure preview; the
      // user reacts in chat.
      return null;
    }
    if (proposal.kind === 'test_call_summary') {
      // ADR-0049 test-call summaries render as a result card inline in
      // GoalsDialogPage. No global side-effect when the user accepts —
      // the user reads the summary and follows up in chat. Same shape
      // as `simulated_call_transcript` above.
      return null;
    }
    // exhaustiveness
    const _exhaustive: never = proposal;
    void _exhaustive;
    return null;
  }

  async function navigate(target: NavigateTarget, projectId?: string): Promise<void> {
    switch (target) {
      case 'dashboard':
        await router.push({ name: 'dashboard' });
        return;
      case 'quickstart':
        await router.push({ name: 'quickstart' });
        return;
      case 'project_overview':
      case 'project_agent':
      case 'project_calls':
      case 'project_connections':
      case 'project_settings':
        if (!projectId) {
          // Should be filtered server-side; defensive fallback.
          await router.push({ name: 'dashboard' });
          return;
        }
        // ProjectDetailPage owns the tab state — we route to the page and
        // pass the desired tab as a hash. The page reads it on mount.
        // (The current ProjectDetailPage uses internal v-model on Tabs;
        // a follow-up will make tabs driven by the route hash, then this
        // hash will activate the right tab automatically.)
        await router.push({
          name: 'project-detail',
          params: { projectId },
          hash: tabHash(target),
        });
        return;
      case 'project_goal_new':
        // Per ADR-0040 §1.4 recursion. AI-first surface for creating a
        // new ProjectGoal. Always project-scoped; defensive fallback to
        // dashboard when projectId is missing (the server shouldn't emit
        // this target without an active project, but belt-and-suspenders).
        if (!projectId) {
          await router.push({ name: 'dashboard' });
          return;
        }
        await router.push({ name: 'goal-new', params: { projectId } });
        return;
      case 'project_plugin_new':
        // Per ADR-0062 §1.4 recursion. AI-first surface for wiring a
        // Plugin Implementation; mirrors goal-new.
        if (!projectId) {
          await router.push({ name: 'dashboard' });
          return;
        }
        await router.push({ name: 'plugin-configuration-new', params: { projectId } });
        return;
      default: {
        const _exhaustive: never = target;
        void _exhaustive;
      }
    }
  }

  return { apply };
}

function tabHash(target: NavigateTarget): string {
  switch (target) {
    case 'project_overview':
      return '#overview';
    case 'project_agent':
      return '#agent';
    case 'project_calls':
      return '#calls';
    case 'project_connections':
      return '#connections';
    case 'project_settings':
      return '#settings';
    default:
      return '';
  }
}
